import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Loader, Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Table, TableColumn } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { WebsiteIcon } from "@/components/WebsiteIcon/WebsiteIcon";
import { ensureHttpProtocol } from "@/utils/url";

import { useLazyGetTestCompanyCategoryResultsQuery } from "@/pages/Private/redux/score/score.api";

import { TestCompanyCategoryResults } from "../../schema/score.schema";

interface SegmentTestModalProps {
	onClose: () => void;
	/** Adds the AI's suggested segment to the draft. */
	onApplySuggestion: (suggestion: { name?: string; description?: string }) => void;
}

const PAGE_SIZE = 5;

/**
 * "Test segments" (carried over from the old page, PREC-4055): classifies five
 * random companies against the saved segments and shows what the AI made of
 * them, with the option to adopt a segment it suggests.
 */
export const SegmentTestModal: FunctionComponent<SegmentTestModalProps> = ({
	onClose,
	onApplySuggestion,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`leadScoring.${key}`);
	const [page, setPage] = useState(1);
	const [rows, setRows] = useState<TestCompanyCategoryResults[]>([]);
	const [getResults, { isFetching }] = useLazyGetTestCompanyCategoryResultsQuery();

	useEffect(() => {
		getResults({ page, limit: PAGE_SIZE })
			.unwrap()
			.then(setRows)
			.catch(() => setRows([]));
	}, [page, getResults]);

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: ts("name"),
				uid: "name",
				width: "200px",
				render: (row: TestCompanyCategoryResults) => (
					<div className="flex flex-col">
						<div className="flex items-center gap-1 text-sm font-bold text-text">
							{row.organization?.name}
							{row.organization?.linkedinUrl ? (
								<a
									href={ensureHttpProtocol(row.organization.linkedinUrl)}
									rel="noreferrer"
									target="_blank"
								>
									<LinkedinIcon status="checked" />
								</a>
							) : (
								<LinkedinIcon status="failed" />
							)}
							{row.organization?.websiteUrl || row.organization?.primaryDomain ? (
								<a
									href={ensureHttpProtocol(
										row.organization.websiteUrl || row.organization.primaryDomain
									)}
									rel="noreferrer"
									target="_blank"
								>
									<WebsiteIcon status="checked" />
								</a>
							) : (
								<WebsiteIcon status="failed" />
							)}
						</div>
						{row.organization?.estimatedNumEmployees && (
							<div className="text-xs text-text-meta">
								{ts("employees")}: {row.organization.estimatedNumEmployees}
							</div>
						)}
						{row.organization?.country && (
							<div className="text-xs text-text-meta">
								{ts("hqCountry")}: {row.organization.country}
							</div>
						)}
					</div>
				),
			},
			{
				title: ts("categorizedAs"),
				uid: "category",
				width: "200px",
				render: (row: TestCompanyCategoryResults) =>
					row.scoreCompanyCategory?.type ? (
						<div className="flex flex-col">
							<div className="text-sm font-bold text-text">{row.result}</div>
							<div className="text-xs text-text-meta">{row.scoreCompanyCategory.type}</div>
						</div>
					) : (
						<div className="flex flex-col gap-2">
							<span className="w-fit rounded-lg border border-status-error bg-background-warning px-2 py-1 text-sm text-status-error">
								{ts("disqualified")}
							</span>
							{row.suggestedNewSubCategory && (
								<Tooltip
									content={
										<div className="flex flex-col gap-1">
											<div className="text-sm text-text-meta">
												{ts("anotherCategorySuggestion")}
											</div>
											<div className="text-sm font-bold text-text">
												{row.suggestedNewSubCategory.name}
											</div>
											<div className="text-xs text-text">
												{row.suggestedNewSubCategory.description}
											</div>
										</div>
									}
								>
									<button
										className="flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-blue-action bg-white p-2 text-sm text-blue-action hover:bg-blue-action hover:text-white"
										type="button"
										onClick={() => onApplySuggestion(row.suggestedNewSubCategory ?? {})}
									>
										<Icon icon={faSparkles} />
										{ts("applyNewSubCategory")}
									</button>
								</Tooltip>
							)}
						</div>
					),
			},
			{
				title: ts("reasoning"),
				uid: "reason",
				width: "380px",
				render: (row: TestCompanyCategoryResults) => (
					<Tooltip content={row.reason}>
						<div className="line-clamp-3 text-sm text-text-meta">{row.reason}</div>
					</Tooltip>
				),
			},
		],
		[t]
	);

	return (
		<Modal
			isOpened
			handleClose={onClose}
			isLoading={isFetching}
			size="lg"
			subtitle={ts("testCompanyText")}
			title={ts("segments.testTitle")}
		>
			<div className="flex w-full flex-col">
				{isFetching ? (
					<div className="flex w-full items-center justify-center gap-2 py-6">
						<Loader />
						{ts("mightTakeAMinute")}
					</div>
				) : (
					<>
						<Table hideFooter hidePagination columns={columns} data={rows} isLoading={false} />
						<div className="mt-3 flex gap-3">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								disabled={page === 1}
								size={ButtonSize.S}
								title={ts("prevCompanies")}
								onClick={() => setPage((current) => current - 1)}
							/>
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title={ts("nextCompanies")}
								onClick={() => setPage((current) => current + 1)}
							/>
						</div>
					</>
				)}
			</div>
		</Modal>
	);
};
